import React from 'react';
import { Button } from './ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    
    // Clear service worker and reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }
    
    // Force reload from server
    window.location.href = window.location.href;
  };

  handleContinue = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-center text-slate-800 mb-3">
              Oops! Something went wrong
            </h1>
            
            <p className="text-center text-slate-600 mb-6">
              The app encountered an unexpected error. This usually happens when cached data becomes corrupted.
            </p>

            <div className="space-y-3">
              <Button
                onClick={this.handleReload}
                className="w-full bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Clear Cache & Reload
              </Button>
              
              <Button
                onClick={this.handleContinue}
                variant="outline"
                className="w-full"
                size="lg"
              >
                Return to Login
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-xs">
                <summary className="cursor-pointer text-slate-500 mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="bg-slate-100 p-3 rounded overflow-auto text-red-600">
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <p className="text-xs text-center text-slate-500 mt-6">
              If this problem persists, try reinstalling the app from your home screen.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
